/**
 * Timezone Utilities - Simple Best Practice Approach
 *
 * Best Practice:
 * 1. Store all dates in UTC on the server (ISO 8601 format)
 * 2. JavaScript Date automatically handles browser timezone
 * 3. Use toISOString() when sending to server (converts to UTC)
 * 4. Use new Date(utcString) when receiving from server (auto-converts to local)
 *
 * This is the simplest and most intuitive approach:
 * - User sees times in THEIR browser's timezone
 * - Server stores times in UTC
 * - No manual timezone conversions needed
 */

/**
 * Converts a Date object to UTC ISO string for server storage.
 * Use this when sending dates to the server.
 *
 * WORKAROUND: Server incorrectly applies timezone conversion again,
 * so we send the local time components as if they were UTC.
 * This compensates for the server's double conversion.
 *
 * @param date - Date object from form/datepicker
 * @returns ISO 8601 string with local time components
 *
 * @example
 * const utcString = toUTCString(selectedDate);
 * // User selected "5:00 AM" in Kiev (UTC+2)
 * // Instead of "2025-12-10T03:00:00Z" (correct UTC)
 * // Returns "2025-12-10T05:00:00Z" (local time as UTC)
 * // Server will subtract 2 hours → stores "2025-12-10T03:00:00Z" (correct!)
 */
export function toUTCString(date: Date | string | null | undefined): string | null {
  if (!date) return null;

  const dateObj = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(dateObj.getTime())) return null;

  // WORKAROUND: Send local time components as UTC to compensate for server's conversion
  // Extract local time components
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");

  // Return local time as if it were UTC
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
}

/**
 * Converts a UTC string from server to Date object for display.
 * The returned Date will automatically display in browser's local timezone.
 *
 * @param utcString - ISO 8601 UTC string from server
 * @returns Date object (displays in browser's local timezone)
 *
 * @example
 * const displayDate = fromUTCString("2024-01-15T15:00:00Z");
 * // In New York browser: shows as "10:00 AM"
 * // In Los Angeles browser: shows as "7:00 AM"
 */
export function fromUTCString(utcString: string | Date | null | undefined): Date | null {
  if (!utcString) return null;

  if (utcString instanceof Date) return utcString;

  // Normalize UTC string format for consistent parsing across browsers
  // Some browsers have issues with "+00:00" format, so we convert to "Z"
  let normalizedString = utcString;
  if (typeof utcString === "string") {
    // Replace "+00:00" or "-00:00" with "Z" for proper UTC parsing
    normalizedString = utcString.replace(/[+-]00:00$/, "Z");
  }

  const date = new Date(normalizedString);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

/**
 * Formats a UTC date string for display in the browser's local timezone.
 *
 * @param utcString - ISO 8601 UTC string from server
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string in local timezone
 */
export function formatUTCDate(
  utcString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
): string {
  if (!utcString) return "";

  const date = new Date(utcString);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", options).format(date);
}

/**
 * Gets the browser's current timezone identifier.
 *
 * @returns IANA timezone identifier (e.g., "America/New_York")
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Gets the browser's current timezone offset in hours.
 *
 * @returns Offset in hours (e.g., -5 for EST)
 */
export function getBrowserTimezoneOffset(): number {
  return -new Date().getTimezoneOffset() / 60;
}
